/**
 * Phase 8.7 — notification centre client.
 *
 * Owns:
 *
 *   * REST wrappers around /notifications, /me/notification-prefs.
 *   * A singleton WebSocket on /ws/notifications. Auto-reconnects via
 *     TrivianaSocket; sends a periodic ping for LB keepalive.
 *   * A tiny pub/sub the bell badge + centre subscribe to: unread count,
 *     newly-arrived notifications, mark-read-side updates.
 *
 * Why a singleton: the bell badge appears on every authed screen and the
 * centre subscribes only when /notifications is open. Keeping the socket
 * and unread cache outside the route handler means navigating between
 * screens never drops the live stream, and the badge stays warm.
 */

import { api } from '../api.js';
import { auth } from '../auth.js';
import { TrivianaSocket } from '../ws.js';
import { startFlashToast, stopFlashToast } from './flash_toast.js';

// Public-facing WS event types — kept in lockstep with
// app/services/notifications.py WS_TYPE_* constants.
export const WS_TYPE_NEW = 'notification.new';
export const WS_TYPE_UNREAD_COUNT = 'notification.unread_count';
export const WS_TYPE_TOAST = 'notification.toast';
// Phase 8.12 slice 3/5 — messaging piggy-backs on this socket for the
// per-conversation unread badge (chat icon on /home). Not persisted;
// forwarded raw to `lib/messaging.js` via a 'chat_unread' event.
export const WS_TYPE_CHAT_UNREAD = 'chat.unread';

// REST surface ---------------------------------------------------------------

export function fetchNotifications({ before = null, limit = 30 } = {}) {
  const params = new URLSearchParams();
  if (before) params.set('before', before);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return api(`/notifications${qs ? `?${qs}` : ''}`);
}

export function fetchUnreadCount() {
  return api('/notifications/unread-count');
}

export function markRead(notificationId) {
  return api(`/notifications/${encodeURIComponent(notificationId)}/read`, {
    method: 'POST',
  });
}

export function markAllRead() {
  return api('/notifications/mark-all-read', { method: 'POST' });
}

export function fetchPrefs() {
  return api('/me/notification-prefs');
}

export function patchPref(type, body) {
  return api(`/me/notification-prefs/${encodeURIComponent(type)}`, {
    method: 'PATCH',
    body,
  });
}

// Global state ---------------------------------------------------------------

let _unreadCount = 0;
const _listeners = new Set();
let _ws = null;
let _pingTimer = null;
let _started = false;

function _emit(event) {
  for (const fn of _listeners) {
    try {
      fn(event);
    } catch (err) {
      console.error('notifications listener error', err);
    }
  }
}

export function getUnreadCount() {
  return _unreadCount;
}

function _setUnreadCount(n) {
  // Coerce to a non-negative integer — server is authoritative but we
  // double-check so a malformed message doesn't poison the badge.
  const next = Math.max(0, Math.floor(Number(n) || 0));
  if (next === _unreadCount) return;
  _unreadCount = next;
  _emit({ kind: 'unread', count: _unreadCount });
}

/**
 * Subscribe to notification events. Returns an unsubscribe function.
 *
 * Events:
 *   { kind: 'unread', count }            — badge value changed
 *   { kind: 'new', notification, count } — a new notification arrived
 *   { kind: 'toast', ...payload }        — flash-toast publish (commit 6
 *                                          renders these)
 */
export function onNotifications(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export function decrementUnreadOptimistically() {
  // Used by the centre page after a successful mark-read so the badge
  // drops without waiting for the bus echo (the WS push will arrive
  // moments later and reconcile to the server's authoritative count).
  if (_unreadCount > 0) _setUnreadCount(_unreadCount - 1);
}

export function setUnreadCountFromServer(n) {
  // Public hook so the centre page's initial GET can prime the count
  // (e.g. on a cold visit where the WS hasn't yet delivered the snapshot).
  _setUnreadCount(n);
}

// WebSocket lifecycle --------------------------------------------------------

const PING_INTERVAL_MS = 25_000;

function _handleMessage(msg) {
  if (!msg || typeof msg !== 'object') return;
  switch (msg.type) {
    case WS_TYPE_UNREAD_COUNT:
      _setUnreadCount(msg.count);
      break;
    case WS_TYPE_NEW: {
      // The server sends the new unread_count alongside the notification
      // so the badge updates before the centre re-fetches the list.
      if (typeof msg.unread_count === 'number') {
        _setUnreadCount(msg.unread_count);
      }
      _emit({
        kind: 'new',
        notification: msg.notification || null,
        count: _unreadCount,
      });
      break;
    }
    case WS_TYPE_TOAST:
      // Commit 6 wires the flash-toast UI; we surface the event for
      // listeners now so the wiring lands incrementally.
      _emit({ kind: 'toast', ...msg });
      break;
    case WS_TYPE_CHAT_UNREAD:
      // Forwarded to `lib/messaging.js` which owns per-conversation
      // accumulation + the Home badge state. Kept out of this file's
      // own state store because the badge semantics (per-conv rather
      // than one total) differ from the notification centre badge.
      _emit({
        kind: 'chat_unread',
        conversationId: msg.conversation_id,
        unreadCount: Number(msg.unread_count) || 0,
      });
      break;
    default:
      // Unknown event types are silently ignored — the WS protocol is
      // versionless and the server may add new types ahead of clients.
      break;
  }
}

function _connect() {
  if (_ws) return;
  _ws = new TrivianaSocket('/ws/notifications');
  _ws.on('message', _handleMessage);
  _ws.on('open', () => {
    if (_pingTimer) clearInterval(_pingTimer);
    _pingTimer = setInterval(() => {
      if (_ws) _ws.send({ type: 'ping' });
    }, PING_INTERVAL_MS);
  });
  _ws.on('close', () => {
    if (_pingTimer) {
      clearInterval(_pingTimer);
      _pingTimer = null;
    }
    // TrivianaSocket auto-reconnects with backoff; we keep `_ws` set so
    // the next connect attempt reuses the wrapper.
  });
  _ws.connect();
}

function _disconnect() {
  if (_pingTimer) {
    clearInterval(_pingTimer);
    _pingTimer = null;
  }
  if (_ws) {
    _ws.close();
    _ws = null;
  }
  _setUnreadCount(0);
}

/**
 * Boot the notifications subsystem for an authed session. Idempotent —
 * safe to call from `main.js` on every auth change.
 *
 * Primes the unread count via REST so the badge renders immediately,
 * then opens the WS so subsequent updates land in real time.
 */
export async function ensureNotificationsStarted() {
  if (!auth.isAuthenticated()) {
    if (_started) {
      _started = false;
      _disconnect();
    }
    return;
  }
  if (_started) return;
  _started = true;
  // Prime via REST — the WS will deliver its own snapshot on connect
  // a moment later (idempotent) but this avoids a flash of zero-count
  // on a cold visit.
  try {
    const res = await fetchUnreadCount();
    _setUnreadCount(res?.count || 0);
  } catch {
    // Best-effort: a 401 here just means the WS will fail too, which
    // triggers its own backoff. No need to surface to the user.
  }
  _connect();
  // Subscribe the global flash-toast listener so duel-challenge events
  // surface as a slim slide-in banner on whichever screen the user is on.
  startFlashToast();
}

export function stopNotifications() {
  // Public hook for sign-out flows that want to drop the socket
  // explicitly rather than wait for the auth-change callback.
  _started = false;
  _disconnect();
  stopFlashToast();
}
